import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Camera,
  ClipboardList,
  Clock,
  Coffee,
  Hammer,
  ImageIcon,
  ImageOff,
  Loader2,
  LogOut,
  MessageSquare,
  Pause,
  Play,
  Send,
  ShieldCheck,
  Square,
  Trash2,
  ZoomIn,
  CalendarDays,
  Wallet,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Toaster, toast } from 'sonner';
import {
  fetchPortalRoster,
  fetchCrewMessages,
  fetchCrewPhotos,
  fetchTimeEntriesForCrew,
  fetchAssignmentsForCrew,
  fetchActiveJobsForCrew,
  findCrewPortalByCode,
  buildCrewHoursSummary,
  buildCrewSchedule,
  buildCrewJobNotes,
  sendCrewMessage,
  markCrewMessagesRead,
  uploadCrewPhoto,
  deleteCrewPhoto,
  formatUsd,
  loadCrewPortalByCode,
  sendCrewPortalMessage,
  logCrewPortalTime,
  uploadCrewPortalPhoto,
  deleteCrewPortalPhoto,
  markCrewPortalRead,
} from '@/lib/crewPortalData';
import { supabase } from '@/lib/supabaseClient';
import { parseContractorDate } from '@/lib/jobsData';
import * as pqi from '@/lib/pqiTracker';
import CrewRemindersCard from '@/components/reminders/CrewRemindersCard';
import { forgetCrewHome } from '@/lib/reminders';

/* -------------------------------------------------------------------------
 * Code-entry screen
 * ----------------------------------------------------------------------- */
const CodeEntry = ({ value, setValue, onSubmit, error, busy }) => {
  const inputRef = useRef(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue(v);
    if (v.length === 6) onSubmit(v);
  };
  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center px-4 bg-[hsl(var(--background))] text-foreground"
      data-testid="crew-portal-code-entry"
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
            Crew Console
          </div>
          <h1
            className="mt-2 text-3xl sm:text-4xl tracking-[0.18em] uppercase text-foreground"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontWeight: 700 }}
          >
            K.O. CO+RE Systems
          </h1>
          <div className="mt-4 text-sm text-muted-foreground">
            Enter your 6-digit access code to clock in.
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (value.length === 6) onSubmit(value);
          }}
          noValidate
        >
          <Input
            ref={inputRef}
            value={value}
            onChange={handleChange}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            className="h-16 text-center text-3xl tracking-[0.4em] font-mono bg-[hsl(var(--card))] border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] focus-visible:ring-[hsl(var(--primary))]/40"
            maxLength={6}
            data-testid="crew-portal-code-input"
            aria-label="6-digit access code"
          />
          <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
            <ShieldCheck size={12} className="text-[hsl(var(--success))]" />
            Crew-only access
          </div>
          {error ? (
            <Alert
              className="mt-4 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
              data-testid="crew-portal-code-error"
            >
              <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          ) : null}
          <Button
            type="submit"
            disabled={busy || value.length !== 6}
            className="mt-5 w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.18em] text-xs"
            data-testid="crew-portal-code-submit"
          >
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
            Enter portal
          </Button>
        </form>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Clock tab — clock in/out + break + timer + today log
 * ----------------------------------------------------------------------- */
const STATE_KEY = (crewId) => `kocore.crew.timer.${crewId}`;

const formatHHMMSS = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((v) => String(v).padStart(2, '0')).join(':');
};

// Always-available "virtual" time buckets that aren't tied to a client job.
// SHOP = shop/yard time; ADMIN = office/admin time. Logged by name only (the
// crew_portal_log_time RPC takes a job name, not a job_id).
const PORTAL_VIRTUAL_JOBS = {
  SHOP: { id: '__SHOP__', name: 'SHOP' },
  ADMIN: { id: '__ADMIN__', name: 'ADMIN' },
};

const ClockTab = ({ crewConfig, jobs, hoursSummary, onTimeEntryAdded, portalCode }) => {
  const [running, setRunning] = useState(false);
  const [paused, setPaused] = useState(false);
  const [pickedJob, setPickedJob] = useState(jobs?.[0]?.id || '__SHOP__');
  const [pickedCoId, setPickedCoId] = useState('');
  const [startedAt, setStartedAt] = useState(null);
  const [pausedAt, setPausedAt] = useState(null);
  const [accumPaused, setAccumPaused] = useState(0);
  const [, setTick] = useState(0); // value unused; each tick just re-renders
  const [submitting, setSubmitting] = useState(false);

  // Restore state
  useEffect(() => {
    if (!crewConfig?.crew_id) return;
    try {
      const raw = localStorage.getItem(STATE_KEY(crewConfig.crew_id));
      if (raw) {
        const parsed = JSON.parse(raw);
        setRunning(!!parsed.running);
        setPaused(!!parsed.paused);
        setPickedJob(parsed.pickedJob || jobs?.[0]?.id || '');
        setStartedAt(parsed.startedAt || null);
        setPausedAt(parsed.pausedAt || null);
        setAccumPaused(Number(parsed.accumPaused) || 0);
      }
    } catch (e) {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewConfig?.crew_id]);

  // Persist state
  useEffect(() => {
    if (!crewConfig?.crew_id) return;
    try {
      localStorage.setItem(
        STATE_KEY(crewConfig.crew_id),
        JSON.stringify({ running, paused, pickedJob, startedAt, pausedAt, accumPaused })
      );
    } catch (e) {
      /* noop */
    }
  }, [running, paused, pickedJob, startedAt, pausedAt, accumPaused, crewConfig?.crew_id]);

  // Tick every second when running
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [running]);

  // Recomputed on every render on purpose: the one-second tick above only
  // exists to re-render so Date.now() moves. Memoizing it would need `tick`
  // as a fake dependency, which is what the lint rule objected to.
  const elapsedSeconds = (() => {
    if (!running || !startedAt) return 0;
    const now = Date.now();
    let pausedTotal = accumPaused;
    if (paused && pausedAt) pausedTotal += now - pausedAt;
    return Math.max(0, Math.floor((now - startedAt - pausedTotal) / 1000));
  })();

  // Picker list = virtual buckets + real active jobs. SHOP is always present;
  // ADMIN only for full-access crew (clock-only members just get SHOP).
  const pickJobs = useMemo(() => {
    const virtuals = [PORTAL_VIRTUAL_JOBS.SHOP];
    if (crewConfig?.access !== 'clock') {
      virtuals.push(PORTAL_VIRTUAL_JOBS.ADMIN);
    }
    return [...virtuals, ...(jobs || [])];
  }, [jobs, crewConfig?.access]);

  const pickedJobObj = pickJobs.find((j) => j.id === pickedJob) || null;

  // Billable T&M change orders on the picked job (crew can scope time to them).
  const tmCos = (
    Array.isArray(pickedJobObj?.change_orders) ? pickedJobObj.change_orders : []
  ).filter(
    (c) => (c?.type || 'fixed') === 'tm' && (c?.status || 'pending') !== 'denied'
  );

  const handlePickJob = (id) => {
    setPickedJob(id);
    setPickedCoId(''); // reset CO scope when job changes
  };

  const reset = () => {
    setPickedCoId('');
    setRunning(false);
    setPaused(false);
    setStartedAt(null);
    setPausedAt(null);
    setAccumPaused(0);
  };

  const handleStart = () => {
    if (!pickedJob) {
      toast.error('Pick an active job first.');
      return;
    }
    setRunning(true);
    setPaused(false);
    setStartedAt(Date.now());
    setPausedAt(null);
    setAccumPaused(0);
    toast.success('Clocked in');
  };

  const handleBreakToggle = () => {
    if (!running) return;
    if (!paused) {
      setPaused(true);
      setPausedAt(Date.now());
      toast.message('Break started');
    } else {
      const now = Date.now();
      setAccumPaused((p) => p + (now - (pausedAt || now)));
      setPaused(false);
      setPausedAt(null);
      toast.message('Back on the clock');
    }
  };

  const handleStop = async () => {
    if (!running) return;
    const seconds = elapsedSeconds;
    if (seconds < 30) {
      // Allow stop but warn
      toast.message('Less than 30s logged');
    }
    const hours = Math.round((seconds / 3600) * 100) / 100;
    const cost = Math.round(hours * (Number(crewConfig.rate) || 0) * 100) / 100;
    const today = new Date().toISOString().slice(0, 10);
    const payload = {
      crew_id: crewConfig.crew_id,
      name: crewConfig.crew_name,
      job: pickedJobObj?.name || '',
      date: today,
      hours,
      cost,
      created_at: new Date().toISOString(),
    };
    const co =
      pickedCoId && pickedCoId !== '__none__'
        ? tmCos.find((c) => c.id === pickedCoId)
        : null;
    setSubmitting(true);
    try {
      const result = await logCrewPortalTime(portalCode, {
        jobName: pickedJobObj?.name || '',
        hours,
        cost,
        date: today,
        coId: co?.id || null,
        coDesc: co?.desc || null,
      });
      if (!result.ok) throw new Error(result.error || 'log_failed');
      toast.success(`Clocked out \u00b7 ${hours.toFixed(2)}h logged`);
      onTimeEntryAdded?.(result.time_entry);
      // PQI: silent telemetry on time logged
      pqi.trackTimeLogged({
        jobName: pickedJobObj?.name || '',
        hours,
        cost,
        crewName: crewConfig.crew_name,
      });
      reset();
    } catch (e) {
      toast.error(e?.message || 'Failed to log time entry.');
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const todayLog = (hoursSummary?.entries || []).filter((e) => e.date === today);
  const todayHours = todayLog.reduce((s, e) => s + (Number(e.hours) || 0), 0);
  const todayCost = todayLog.reduce((s, e) => s + (Number(e.cost) || 0), 0);

  return (
    <div className="space-y-3" data-testid="crew-portal-clock-tab">
      {/* Hero clock card */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4 sm:p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Status
          </div>
          <div className="mt-1 flex items-center gap-2">
            <span
              className={cn(
                'inline-block h-2.5 w-2.5 rounded-full',
                running && !paused
                  ? 'bg-[hsl(var(--success))]'
                  : running && paused
                  ? 'bg-[hsl(var(--warning))]'
                  : 'bg-[hsl(var(--muted-foreground))]'
              )}
              aria-hidden
            />
            <span
              className="text-base font-semibold uppercase tracking-[0.18em] text-foreground"
              data-testid="crew-portal-status"
            >
              {running && !paused
                ? 'On the clock'
                : running && paused
                ? 'On break'
                : 'Clocked out'}
            </span>
          </div>
          <div
            className="mt-3 text-5xl sm:text-6xl font-mono tabular-nums tracking-[0.08em] text-foreground"
            data-testid="crew-portal-timer"
          >
            {formatHHMMSS(elapsedSeconds)}
          </div>
          {running && pickedJobObj ? (
            <div className="mt-1 text-xs text-muted-foreground truncate">
              {pickedJobObj.name}
            </div>
          ) : null}

          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              Active job
            </label>
            <Select
              value={pickedJob}
              onValueChange={handlePickJob}
              disabled={running}
            >
              <SelectTrigger
                className="h-12 mt-1 bg-[hsl(var(--muted))] border-border text-base"
                data-testid="crew-portal-clock-job"
              >
                <SelectValue placeholder="Pick a job" />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                {pickJobs.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    No jobs available.
                  </div>
                ) : (
                  pickJobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>

            {/* Optional CO scope — only when the picked job has T&M change orders */}
            {tmCos.length > 0 ? (
              <div className="mt-3">
                <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Change order (optional)
                </label>
                <Select
                  value={pickedCoId}
                  onValueChange={setPickedCoId}
                  disabled={running}
                >
                  <SelectTrigger
                    className="h-12 mt-1 bg-[hsl(var(--muted))] border-border text-base"
                    data-testid="crew-portal-clock-co"
                  >
                    <SelectValue placeholder="Base job — no change order" />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                    <SelectItem value="__none__">
                      Base job — no change order
                    </SelectItem>
                    {tmCos.map((co) => (
                      <SelectItem key={co.id} value={co.id}>
                        {co.desc}
                        {co.nte ? ` · cap ${formatUsd(co.nte)}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>

          {/* Action buttons */}
          <div className="mt-4 grid grid-cols-1 gap-2">
            {!running ? (
              <Button
                type="button"
                onClick={handleStart}
                disabled={!pickedJob}
                className="h-14 text-base bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] border border-[hsl(var(--success))]/40 hover:bg-[hsl(var(--success))]/30 uppercase tracking-[0.18em]"
                data-testid="crew-portal-clock-in"
              >
                <Play size={18} className="mr-2" />
                Clock in
              </Button>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  onClick={handleBreakToggle}
                  className={cn(
                    'h-14 text-base uppercase tracking-[0.16em] border',
                    paused
                      ? 'bg-[hsl(var(--success))]/20 text-[hsl(var(--success))] border-[hsl(var(--success))]/40 hover:bg-[hsl(var(--success))]/30'
                      : 'bg-[hsl(var(--warning))]/20 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40 hover:bg-[hsl(var(--warning))]/30'
                  )}
                  data-testid="crew-portal-clock-break"
                >
                  {paused ? (
                    <>
                      <Play size={16} className="mr-2" />
                      Resume
                    </>
                  ) : (
                    <>
                      <Coffee size={16} className="mr-2" />
                      Break
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={handleStop}
                  disabled={submitting}
                  className="h-14 text-base bg-[hsl(var(--destructive))]/20 text-[hsl(var(--destructive))] border border-[hsl(var(--destructive))]/40 hover:bg-[hsl(var(--destructive))]/30 uppercase tracking-[0.16em]"
                  data-testid="crew-portal-clock-out"
                >
                  {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Square size={16} className="mr-2" />}
                  Clock out
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Today log */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-[hsl(var(--primary))]" />
              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                Today
              </span>
            </div>
            <div className="text-[11px] text-muted-foreground tabular-nums">
              {todayHours.toFixed(2)}h {'\u00b7'} {formatUsd(todayCost)}
            </div>
          </div>
          {todayLog.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No hours logged today yet.
            </div>
          ) : (
            <div className="space-y-2" data-testid="crew-portal-today-log">
              {todayLog.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center justify-between gap-2 border border-border rounded-md bg-[hsl(var(--muted))]/40 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-foreground truncate">
                      {e.job || 'Unassigned'}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {Number(e.hours).toFixed(2)}h
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-foreground tabular-nums shrink-0">
                    {formatUsd(e.cost)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Schedule tab
 * ----------------------------------------------------------------------- */
const ScheduleTab = ({ schedule = [] }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const grouped = useMemo(() => {
    const byDate = new Map();
    for (const a of schedule) {
      const dateStr = a?.date || 'unscheduled';
      if (!byDate.has(dateStr)) byDate.set(dateStr, []);
      byDate.get(dateStr).push(a);
    }
    return Array.from(byDate.entries()).map(([d, items]) => ({ date: d, items }));
  }, [schedule]);

  if (schedule.length === 0) {
    return (
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="crew-portal-schedule-tab">
        <CardContent className="p-6 text-center">
          <CalendarDays size={20} className="mx-auto text-muted-foreground mb-2" />
          <div className="text-sm text-muted-foreground">
            No assignments yet. Your contractor will add jobs to your schedule.
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3" data-testid="crew-portal-schedule-tab">
      {grouped.map((g) => {
        const d = parseContractorDate(g.date);
        const isToday = d && d.getTime() === today.getTime();
        return (
          <Card
            key={g.date}
            className={cn(
              'border bg-[hsl(var(--card))]',
              isToday ? 'border-[hsl(var(--primary))]/40' : 'border-[hsl(var(--hairline))]'
            )}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-foreground uppercase tracking-[0.18em]">
                  {g.date || 'Unscheduled'}
                </div>
                {isToday ? (
                  <Badge
                    variant="outline"
                    className="uppercase text-[9px] tracking-[0.18em] border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10"
                  >
                    Today
                  </Badge>
                ) : null}
              </div>
              <div className="space-y-2">
                {g.items.map((a) => (
                  <div
                    key={a.id}
                    className="rounded-md border border-border bg-[hsl(var(--muted))]/40 px-3 py-2"
                  >
                    <div className="text-sm font-semibold text-foreground truncate">
                      {a.job_name || a.title || 'Job'}
                    </div>
                    {a.notes ? (
                      <div className="text-[11px] text-muted-foreground whitespace-pre-wrap">
                        {a.notes}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Notes tab — operational only
 * ----------------------------------------------------------------------- */
const NotesTab = ({ notes = [] }) => {
  if (notes.length === 0) {
    return (
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="crew-portal-notes-tab">
        <CardContent className="p-6 text-center">
          <ClipboardList size={20} className="mx-auto text-muted-foreground mb-2" />
          <div className="text-sm text-muted-foreground">
            No operational notes yet.
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2" data-testid="crew-portal-notes-tab">
      {notes.map((n) => (
        <Card key={n.id} className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between mb-1">
              <div className="text-xs font-semibold text-foreground truncate">
                {n.job_name}
              </div>
              <Badge
                variant="outline"
                className="uppercase text-[9px] tracking-[0.16em] border-border text-muted-foreground"
              >
                {(n.type || 'field').replace(/_/g, ' ')}
              </Badge>
            </div>
            <div className="text-sm text-foreground whitespace-pre-wrap">{n.text}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{n.date || ''}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Messages tab
 * ----------------------------------------------------------------------- */
const MessagesTab = ({ messages = [], onSend, sending }) => {
  const [body, setBody] = useState('');
  const scrollRef = useRef(null);
  useEffect(() => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  }, [messages.length]);
  const handle = async () => {
    if (!body.trim()) return;
    await onSend(body.trim());
    setBody('');
  };
  return (
    <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]" data-testid="crew-portal-messages-tab">
      <CardContent className="p-3 sm:p-4">
        <div
          ref={scrollRef}
          className="space-y-2 max-h-[55vh] overflow-auto kc-scroll pr-1 mb-3"
          data-testid="crew-portal-messages-list"
        >
          {messages.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              <MessageSquare size={18} className="mx-auto mb-2 opacity-50" />
              No messages yet.
            </div>
          ) : (
            messages.map((m) => (
              <div
                key={m.id}
                className={cn(
                  'rounded-md border px-3 py-2 max-w-[80%]',
                  m.from === 'crew'
                    ? 'ml-auto border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10'
                    : 'border-border bg-[hsl(var(--muted))]/40'
                )}
              >
                <div className="text-sm text-foreground whitespace-pre-wrap">{m.body}</div>
                <div className="mt-1 text-[10px] text-muted-foreground">
                  {m.from === 'crew' ? 'You' : 'Boss'} {'\u00b7'}{' '}
                  {m.sent_at ? new Date(m.sent_at).toLocaleString() : ''}
                </div>
              </div>
            ))
          )}
        </div>
        <div className="flex items-end gap-2">
          <Textarea
            rows={2}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 bg-[hsl(var(--muted))] border-border text-foreground resize-none"
            data-testid="crew-portal-message-input"
          />
          <Button
            type="button"
            onClick={handle}
            disabled={sending || !body.trim()}
            className="h-10 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="crew-portal-message-send"
          >
            {sending ? <Loader2 size={12} className="mr-2 animate-spin" /> : <Send size={12} className="mr-2" />}
            Send
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

/* -------------------------------------------------------------------------
 * Photos tab
 * ----------------------------------------------------------------------- */
const PhotosTab = ({ photos = [], jobs = [], crewConfig, onUploaded, onDeleted }) => {
  const fileRef = useRef(null);
  const [pickedJob, setPickedJob] = useState('');
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const file = files[0];
      const job = jobs.find((j) => j.id === pickedJob);
      // Pass the raw File to the data layer; it will upload to Supabase Storage
      // and persist only the public URL (no more big data URLs in settings).
      const next = await onUploaded({
        file,
        jobId: job?.id || null,
        jobName: job?.name || '',
        caption,
      });
      setCaption('');
      if (fileRef.current) fileRef.current.value = '';
      toast.success('Photo uploaded');
      return next;
    } catch (e) {
      toast.error(e?.message || 'Failed to upload photo.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await onDeleted(photoId);
      toast.success('Photo deleted');
    } catch (e) {
      toast.error(e?.message || 'Failed to delete photo.');
    }
  };

  return (
    <div className="space-y-3" data-testid="crew-portal-photos-tab">
      {/* Upload card */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-2">
              <Select value={pickedJob} onValueChange={setPickedJob}>
                <SelectTrigger
                  className="h-10 bg-[hsl(var(--muted))] border-border"
                  data-testid="crew-portal-photo-job"
                >
                  <SelectValue placeholder="Tag a job (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border">
                  <SelectItem value="">No job</SelectItem>
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Caption (optional)"
                className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid="crew-portal-photo-caption"
              />
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="crew-portal-photo-file-input"
            />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="h-12 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.18em] text-xs"
              data-testid="crew-portal-photo-upload"
            >
              {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Camera size={14} className="mr-2" />}
              Upload site photo
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Gallery */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-3 sm:p-4">
          {photos.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <ImageOff size={18} className="mx-auto mb-2 opacity-50" />
              No photos uploaded yet.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((p) => (
                <div key={p.id} className="relative group">
                  <button
                    type="button"
                    onClick={() => setOpen(p)}
                    className="block w-full aspect-square rounded border border-border overflow-hidden hover:ring-2 hover:ring-[hsl(var(--primary))]/40"
                    data-testid={`crew-portal-photo-${p.id}`}
                  >
                    <img
                      src={p.url}
                      alt={p.caption || 'Site photo'}
                      className="w-full h-full object-cover"
                    />
                    {p.caption ? (
                      <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 bg-black/70 text-[9px] text-white truncate">
                        {p.caption}
                      </div>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(p.id)}
                    aria-label="Delete photo"
                    className="absolute top-1 right-1 h-6 w-6 rounded bg-black/60 text-[hsl(var(--destructive))] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    data-testid={`crew-portal-photo-delete-${p.id}`}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {open ? (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
          role="dialog"
          aria-modal="true"
          data-testid="crew-portal-photo-modal"
        >
          <img
            src={open.url}
            alt={open.caption || 'Site photo'}
            className="max-h-[90vh] max-w-[95vw] rounded-md shadow-2xl border border-border"
          />
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Hours tab — total + day-by-day breakdown
 * ----------------------------------------------------------------------- */
const HoursTab = ({ hoursSummary, crewConfig }) => {
  if (!hoursSummary) return null;
  return (
    <div className="space-y-3" data-testid="crew-portal-hours-tab">
      <div className="grid grid-cols-2 gap-3">
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Total hours
            </div>
            <div className="mt-1 text-2xl font-semibold text-foreground tabular-nums">
              {(hoursSummary.totalHours || 0).toFixed(2)}h
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Last 7 days: {(hoursSummary.weekHours || 0).toFixed(2)}h
            </div>
          </CardContent>
        </Card>
        <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
          <CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              Total earned
            </div>
            <div className="mt-1 text-2xl font-semibold text-[hsl(var(--success))] tabular-nums">
              {formatUsd(hoursSummary.totalCost || 0)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              @ {formatUsd(crewConfig?.rate || 0)}/h
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-3 sm:p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
            Day by day
          </div>
          {hoursSummary.days.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No hours logged yet.
            </div>
          ) : (
            <div className="space-y-2" data-testid="crew-portal-hours-days">
              {hoursSummary.days.slice(0, 30).map((d) => (
                <div
                  key={d.date}
                  className="flex items-center justify-between gap-3 border border-border rounded-md bg-[hsl(var(--muted))]/40 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-foreground">{d.date}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {(d.jobs || []).join(', ') || 'No job tagged'}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-foreground tabular-nums">
                      {d.hours.toFixed(2)}h
                    </div>
                    <div className="text-[11px] text-[hsl(var(--success))]">
                      {formatUsd(d.cost)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Main view
 * ----------------------------------------------------------------------- */
export default function CrewPortalView() {
  const params = useParams();
  const navigate = useNavigate();

  const [code, setCode] = useState(params.code || '');
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [allMessages, setAllMessages] = useState({});
  const [allPhotos, setAllPhotos] = useState({});
  const [timeEntries, setTimeEntries] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [sendingMsg, setSendingMsg] = useState(false);

  const submitCode = useCallback(async (val) => {
    const v = String(val || code || '').trim();
    if (!/^\d{6}$/.test(v)) {
      setError('Enter the 6-digit code your boss issued.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await loadCrewPortalByCode(v);
      if (!result.ok) {
        if (result.error === 'invalid_code') {
          setError('Code not recognized or portal disabled. Ask your boss for a new one.');
        } else if (result.error === 'rate_limited') {
          setError('Too many attempts. Please wait a few minutes and try again.');
        } else if (result.error === 'portal_unavailable') {
          setError('The portal is temporarily unavailable. Please try again later.');
        } else {
          setError(result.error || 'Something went wrong. Try again.');
        }
        return;
      }
      setJobs(result.active_jobs);
      setTimeEntries(result.time_entries);
      // Schedule + notes still come from direct reads (or empty fallback);
      // the RPC payload deliberately keeps the shape lean.
      try {
        const asg = await fetchAssignmentsForCrew();
        setAssignments(asg);
      } catch (e) { setAssignments([]); }
      setAllMessages({ [result.config.crew_id]: result.messages });
      setAllPhotos({ [result.config.crew_id]: result.photos });
      setConfig(result.config);
      setAuthed(true);
      // Best-effort mark contractor messages as read
      markCrewPortalRead(v).catch(() => { /* noop */ });
      if (params.code !== v) navigate(`/portal/crew/${v}`, { replace: true });
    } catch (e) {
      setError(e?.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }, [code, navigate, params.code]);

  useEffect(() => {
    if (params.code && !authed && !loading) submitCode(params.code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hoursSummary = useMemo(
    () => buildCrewHoursSummary(config, timeEntries),
    [config, timeEntries]
  );
  const schedule = useMemo(
    () => buildCrewSchedule(config, assignments),
    [config, assignments]
  );
  const allJobsForNotes = useMemo(() => jobs, [jobs]);
  const notes = useMemo(
    () => buildCrewJobNotes(allJobsForNotes),
    [allJobsForNotes]
  );

  const myMessages = config ? allMessages[config.crew_id] || [] : [];
  const myPhotos = config ? allPhotos[config.crew_id] || [] : [];

  const handleSendMessage = async (body) => {
    setSendingMsg(true);
    try {
      const result = await sendCrewPortalMessage(config.code, body);
      if (!result.ok) {
        toast.error(result.error || 'Could not send message.');
        return;
      }
      setAllMessages((cur) => {
        const list = cur[config.crew_id] || [];
        return { ...cur, [config.crew_id]: [...list, result.message] };
      });
    } catch (e) {
      toast.error(e?.message || 'Could not send message.');
    } finally {
      setSendingMsg(false);
    }
  };

  const handleUploadPhoto = async ({ file, jobId, jobName, caption }) => {
    const result = await uploadCrewPortalPhoto(config.code, { file, jobId, jobName, caption });
    if (!result.ok) throw new Error(result.error || 'upload_failed');
    setAllPhotos((cur) => {
      const list = cur[config.crew_id] || [];
      return { ...cur, [config.crew_id]: [result.photo, ...list] };
    });
    return result;
  };

  const handleDeletePhoto = async (photoId) => {
    const result = await deleteCrewPortalPhoto(config.code, photoId);
    if (!result.ok) throw new Error(result.error || 'delete_failed');
    setAllPhotos((cur) => {
      const list = (cur[config.crew_id] || []).filter((p) => p.id !== photoId);
      return { ...cur, [config.crew_id]: list };
    });
  };

  const handleTimeEntryAdded = (entry) => {
    setTimeEntries((cur) => [entry, ...cur]);
  };

  const handleLogout = () => {
    forgetCrewHome();
    setAuthed(false);
    setConfig(null);
    setCode('');
    navigate('/portal/crew', { replace: true });
  };

  if (!authed) {
    return (
      <>
        <Toaster theme="dark" position="top-right" />
        <CodeEntry
          value={code}
          setValue={setCode}
          onSubmit={submitCode}
          error={error}
          busy={loading}
        />
      </>
    );
  }

  const isClockOnly = config?.access === 'clock';

  return (
    <div
      className="min-h-[100dvh] bg-[hsl(var(--background))] text-foreground"
      data-testid="crew-portal-view"
    >
      <Toaster theme="dark" position="top-right" />
      <div className="safe-area-top-shield" aria-hidden="true" />
      <header className="sticky top-0 z-40 pt-safe bg-[hsl(var(--card))] border-b border-[hsl(var(--hairline))]">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
              Crew portal {isClockOnly ? '\u00b7 Clock only' : ''}
            </div>
            <div
              className="text-base sm:text-lg tracking-[0.18em] uppercase text-foreground"
              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif", fontWeight: 700 }}
            >
              K.O. CO+RE Systems
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-xs font-semibold text-foreground truncate max-w-[140px] sm:max-w-none">
                {config?.crew_name}
              </div>
              <div className="text-[10px] text-muted-foreground tabular-nums">
                {formatUsd(config?.rate || 0)}/h
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="h-8 border-border text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))] uppercase tracking-[0.16em] text-[10px]"
              data-testid="crew-portal-exit"
            >
              <LogOut size={11} className="mr-1.5" />
              Exit
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 py-4">
        {isClockOnly ? (
          // Clock-only crew sees only the Clock tab
          <ClockTab
            crewConfig={config}
            jobs={jobs}
            hoursSummary={hoursSummary}
            onTimeEntryAdded={handleTimeEntryAdded}
            portalCode={config?.code}
          />
        ) : (
          <Tabs defaultValue="clock" className="w-full" data-testid="crew-portal-tabs">
            <TabsList className="bg-[hsl(var(--muted))] border border-border w-full overflow-x-auto no-scrollbar mb-3">
              <TabsTrigger value="clock" className="text-xs uppercase tracking-[0.16em]">
                <Clock size={12} className="mr-1.5" />
                Clock
              </TabsTrigger>
              <TabsTrigger value="schedule" className="text-xs uppercase tracking-[0.16em]">
                <CalendarDays size={12} className="mr-1.5" />
                Schedule
              </TabsTrigger>
              <TabsTrigger value="notes" className="text-xs uppercase tracking-[0.16em]">
                <ClipboardList size={12} className="mr-1.5" />
                Notes
              </TabsTrigger>
              <TabsTrigger value="messages" className="text-xs uppercase tracking-[0.16em]">
                <MessageSquare size={12} className="mr-1.5" />
                Messages
              </TabsTrigger>
              <TabsTrigger value="photos" className="text-xs uppercase tracking-[0.16em]">
                <ImageIcon size={12} className="mr-1.5" />
                Photos
              </TabsTrigger>
              <TabsTrigger value="hours" className="text-xs uppercase tracking-[0.16em]">
                <Wallet size={12} className="mr-1.5" />
                Hours
              </TabsTrigger>
            </TabsList>

            <TabsContent value="clock">
              <ClockTab
                crewConfig={config}
                jobs={jobs}
                hoursSummary={hoursSummary}
                onTimeEntryAdded={handleTimeEntryAdded}
                portalCode={config?.code}
              />
            </TabsContent>
            <TabsContent value="schedule">
              <ScheduleTab schedule={schedule} />
            </TabsContent>
            <TabsContent value="notes">
              <NotesTab notes={notes} />
            </TabsContent>
            <TabsContent value="messages">
              <MessagesTab
                messages={myMessages}
                onSend={handleSendMessage}
                sending={sendingMsg}
              />
            </TabsContent>
            <TabsContent value="photos">
              <PhotosTab
                photos={myPhotos}
                jobs={jobs}
                crewConfig={config}
                onUploaded={handleUploadPhoto}
                onDeleted={handleDeletePhoto}
              />
            </TabsContent>
            <TabsContent value="hours">
              <HoursTab hoursSummary={hoursSummary} crewConfig={config} />
            </TabsContent>
          </Tabs>
        )}
        <CrewRemindersCard code={config?.code} />
      </main>
    </div>
  );
}
